import { useEffect, useState } from 'react'
import { useLocale } from '@/i18n/locale'
import { DEFAULT_THEME, THEME_COLOR, THEME_KEY, type Theme } from '@/lib/theme'

/**
 * Переключатель темы (light/dark).
 *
 * Хранит явный выбор человека в localStorage под ключом THEME_KEY
 * (src/lib/theme.ts). Ничего не выбрано — светлая тема, настройки ОС
 * не учитываются: решение Тони 2026-09-26 — у всех по умолчанию светлая.
 * Значение применяется через `document.documentElement.dataset.theme`,
 * то же делает inline-скрипт в index.html (см. FOUC-защиту).
 *
 * На мобиле (бургер-меню) передаётся `compact` — растягивается на всю
 * ширину и показывает подпись. В шапке — иконочная кнопка 36×36.
 */

/** Дефолт — светлая (решение Тони 2026-09-26), см. src/lib/theme.ts. */
function readCurrentTheme(): Theme {
  const current = document.documentElement.dataset.theme
  if (current === 'light' || current === 'dark') return current
  try {
    return window.localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : DEFAULT_THEME
  } catch {
    return DEFAULT_THEME
  }
}

/** Цвет адресной строки (Safari/Chrome mobile) под текущую тему. */
function syncThemeColor(theme: Theme) {
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]:not([media])')
  if (meta) meta.content = THEME_COLOR[theme]
}

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme
  try {
    window.localStorage.setItem(THEME_KEY, theme)
  } catch {
    /* приватный режим — просто игнорируем */
  }
  syncThemeColor(theme)
}

export function ThemeToggle({ compact = false }: { compact?: boolean }) {
  // Первый рендер обязан совпасть с серверным, а сервер всегда рисует тему
  // по умолчанию — светлую. Раньше состояние сразу читало тему из DOM: у
  // человека с другой сохранённой темой иконка и подписи расходились с
  // SSR-разметкой, React бросал #418/#423 и перерисовывал всю страницу. Саму
  // тему до отрисовки ставит inline-скрипт в index.html — здесь только
  // догоняем её после гидратации и красим адресную строку.
  const [theme, setTheme] = useState<Theme>(DEFAULT_THEME)
  useEffect(() => {
    const current = readCurrentTheme()
    setTheme(current)
    syncThemeColor(current)
  }, [])

  // Пишем тему только по клику. До 20.09 эффект сохранял её при каждом
  // заходе, и у всех в памяти браузера оказалась тёмная, которую никто не
  // выбирал, — поэтому ключ сменился (src/lib/theme.ts).
  const toggle = () => {
    const chosen: Theme = theme === 'dark' ? 'light' : 'dark'
    applyTheme(chosen)
    setTheme(chosen)
  }

  // Слушателя системной темы нет: все, кто ничего не выбирал, видят одно и
  // то же — светлую. Человек, переключивший тумблер, получает свой выбор
  // навсегда.

  const isEn = useLocale() === 'en'
  const label = isEn
    ? theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'
    : theme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'

  const icon =
    theme === 'dark' ? (
      // Солнце
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
        <g stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M12 3v2" />
          <path d="M12 19v2" />
          <path d="M3 12h2" />
          <path d="M19 12h2" />
          <path d="M5.6 5.6l1.4 1.4" />
          <path d="M17 17l1.4 1.4" />
          <path d="M5.6 18.4l1.4-1.4" />
          <path d="M17 7l1.4-1.4" />
        </g>
      </svg>
    ) : (
      // Луна
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path
          d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
      </svg>
    )

  if (compact) {
    return (
      <button
        type="button"
        onClick={toggle}
        aria-label={label}
        className="
          inline-flex items-center gap-3 py-2
          text-body text-ink
          transition-opacity hover:opacity-70
        "
      >
        <span className="inline-flex items-center justify-center w-8 h-8 rounded-btn border border-divider text-ink">
          {icon}
        </span>
        <span>
          {isEn
            ? theme === 'dark' ? 'Light theme' : 'Dark theme'
            : theme === 'dark' ? 'Светлая тема' : 'Тёмная тема'}
        </span>
      </button>
    )
  }

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={label}
      title={label}
      className="
        inline-flex items-center justify-center w-9 h-9 rounded-btn
        border border-divider text-ink
        transition-colors hover:bg-card
        focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-bg
      "
    >
      {icon}
    </button>
  )
}
